'use client';

import { CircularProgress, Stack, Typography } from '@mui/material';
import type { SqlEngine } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { OptionMenu } from '@/components/option-menu/option-menu';
import { PanelWrapper } from '@/components/panels/panelWrapper';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { dayjs } from '@/utils/dayjs';

import { DeleteSqlEngineDialog } from './deleteSqlEngineDialog';
import { SqlEngineStatusCell } from './sqlEngineStatusCell';
import { WORKER_SIZE_LABELS } from './workerSizeLabels';

interface SqlEnginePanelProps {
  id: string;
  workspaceId: string;
  onClose: () => void;
  onRefresh: () => void;
}

export const SqlEnginePanel = ({
  id,
  workspaceId,
  onClose,
  onRefresh,
}: SqlEnginePanelProps) => {
  const dal = useDAL();
  const { isAuthorized } = useRoles();
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: engineData, isLoading: isEngineLoading } =
    dal.lakehouse.useListSqlEngines(workspaceId);

  const engine = engineData?.sqlEngines?.find((e: SqlEngine) => e.id === id);

  if (isEngineLoading || !engine) {
    return (
      <PanelWrapper
        header={{
          title: 'SQL Engine Details',
          onClose,
        }}
      >
        <Stack alignItems='center' justifyContent='center' flex={1}>
          <CircularProgress />
        </Stack>
      </PanelWrapper>
    );
  }

  const canUpdate = isAuthorized('update:sql_engine');
  const canDelete = isAuthorized('delete:sql_engine');

  const isStarted = engine.status === 'STARTED';
  const isStopped = engine.status === 'STOPPED';
  const canStart = isStopped;
  const isTransitioning =
    engine.status === 'CREATING' ||
    engine.status === 'STARTING' ||
    engine.status === 'STOPPING' ||
    engine.status === 'DELETING';

  const handleStart = async () => {
    if (!workspaceId) return;
    setIsLoading(true);
    try {
      await dal.lakehouse.startSqlEngine(workspaceId, engine.id);
      onRefresh();
    } catch (err) {
      console.error('Failed to start SQL engine:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStop = async () => {
    if (!workspaceId) return;
    setIsLoading(true);
    try {
      await dal.lakehouse.stopSqlEngine(workspaceId, engine.id);
      onRefresh();
    } catch (err) {
      console.error('Failed to stop SQL engine:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <PanelWrapper
        header={{
          title: 'SQL Engine Details',
          onClose,
          actions: isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <OptionMenu
              options={[
                {
                  id: 'start-engine',
                  text: 'Start',
                  icon: 'material-symbols-play-arrow-rounded',
                  menuItemProps: {
                    disabled: !canUpdate || isTransitioning || !canStart,
                    onClick: () => void handleStart(),
                  },
                },
                {
                  id: 'stop-engine',
                  text: 'Stop',
                  icon: 'material-symbols-stop-rounded',
                  menuItemProps: {
                    disabled: !canUpdate || isTransitioning || !isStarted,
                    onClick: () => void handleStop(),
                  },
                },
                {
                  id: 'divider',
                  divider: true,
                },
                {
                  id: 'delete-engine',
                  text: 'Delete',
                  icon: 'material-symbols-delete-outline-rounded',
                  menuItemProps: {
                    disabled: !canDelete || isTransitioning,
                    onClick: () => setDeleteDialogOpen(true),
                  },
                },
              ]}
            />
          ),
        }}
      >
        <Stack gap='24px' padding='24px 40px'>
          {/* Engine Details */}
          <Stack gap='12px'>
            <DetailRow label='ID' value={engine.id} />
            <DetailRow label='Name' value={engine.name} />
            <DetailRow
              label='Size'
              value={WORKER_SIZE_LABELS[engine.workerSize] ?? engine.workerSize}
            />
            <DetailRow
              label='Autoscaling'
              value={engine.autoscaling ? 'Enabled' : 'Disabled'}
            />
            {engine.autoscaling ? (
              <>
                <DetailRow
                  label='Min Workers'
                  value={String(engine.minWorkers)}
                />
                <DetailRow
                  label='Max Workers'
                  value={String(engine.maxWorkers)}
                />
              </>
            ) : (
              <DetailRow label='Workers' value={String(engine.workerCount)} />
            )}
            <DetailRow
              label='Idle Timeout'
              value={
                engine.idleTimeoutMinutes !== null &&
                engine.idleTimeoutMinutes !== undefined
                  ? `${engine.idleTimeoutMinutes} minutes`
                  : 'Disabled'
              }
            />
            <DetailRow
              label='IAM Role'
              value={engine.awsRuntimeConfig?.iamRoleArn ?? '-'}
            />
            <DetailRow
              label='Status'
              value={<SqlEngineStatusCell status={engine.status} />}
            />
            <DetailRow
              label='Created'
              value={dayjs.utc(engine.createdAt).format('MMM D, YYYY HH:mm:ss')}
            />
          </Stack>
        </Stack>
      </PanelWrapper>

      <DeleteSqlEngineDialog
        engine={engine}
        workspaceId={workspaceId}
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={() => {
          onRefresh();
          onClose();
        }}
      />
    </>
  );
};

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => {
  return (
    <Stack direction='row' alignItems='flex-start' gap='16px'>
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{ minWidth: '100px' }}
      >
        {label}
      </Typography>
      {typeof value === 'string' ? (
        <Typography variant='body2' sx={{ wordBreak: 'break-word', flex: 1 }}>
          {value || '-'}
        </Typography>
      ) : (
        (value ?? (
          <Typography variant='body2' sx={{ flex: 1 }}>
            -
          </Typography>
        ))
      )}
    </Stack>
  );
};
